import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";
/**
 * PerformanceAlertsMock
 * - Purpose: stand-alone Performance Alerts dashboard for testing/copy-paste.
 * - Inputs: optional props to override mock data / dates.
 * - Output: renders the same layout as PerformanceAlerts, without Redux/appService.
 */
// Inject the Onest font into <head> so this file is copy/paste friendly.
function ensureOnestFontLoaded() {
  if (typeof document === "undefined") return;
  const fontLinkId = "google-font-onest-css";
  if (document.getElementById(fontLinkId)) return;
  // Preconnects help avoid a noticeable first-render font swap.
  const preconnect1 = document.createElement("link");
  preconnect1.rel = "preconnect";
  preconnect1.href = "https://fonts.googleapis.com";
  const preconnect2 = document.createElement("link");
  preconnect2.rel = "preconnect";
  preconnect2.href = "https://fonts.gstatic.com";
  preconnect2.crossOrigin = "anonymous";
  const css = document.createElement("link");
  css.id = fontLinkId;
  css.rel = "stylesheet";
  css.href =
    "https://fonts.googleapis.com/css2?family=Onest:wght@400;500;600;700&display=swap";
  document.head.appendChild(preconnect1);
  document.head.appendChild(preconnect2);
  document.head.appendChild(css);
}
// Minimal, inlined RCC palette (exact tokens copied from `RevenueCommandCenter/colors.js`).
// Keep this small on purpose: only values used by this dashboard.
const rccColors = {
  neutral: {
    50: "#ECEFF1",
    500: "#607D8B",
    700: "#455A64",
    900: "#263238",
  },
  blue: {
    700: "#1976D2",
    800: "#1565C0",
  },
  orange: {
    700: "#F57C00",
  },
  red: {
    600: "#E53935",
  },
  green: {
    700: "#388E3C",
  },
  materialGrey: {
    0: "#FFFFFF",
    100: "#F5F5F5",
  },
};
const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "100vh",
    padding: "32px 60px",
    maxWidth: "1000px",
    margin: "0 auto",
  },
  headerSection: {
    textAlign: "center",
    marginBottom: "32px",
  },
  title: {
    color: rccColors.neutral[900],
    marginBottom: "8px",
    fontFamily: '"Onest", sans-serif',
  },
  subtitle: {
    color: rccColors.neutral[700],
    maxWidth: "800px",
    margin: "0 auto",
    fontFamily: '"Onest", sans-serif',
  },
  errorState: {
    color: rccColors.red[600],
    textAlign: "center",
    padding: "40px",
    fontFamily: '"Onest", sans-serif',
  },
  emptyState: {
    color: rccColors.neutral[500],
    textAlign: "center",
    padding: "20px",
    fontStyle: "italic",
    fontFamily: '"Onest", sans-serif',
  },
  loadingContainer: {
    padding: "40px",
  },
  contentCard: {
    backgroundColor: rccColors.materialGrey[0],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  // Minimal typography helpers (exact values copied from `RevenueCommandCenter/typography.js`).
  displayMediumSemibold: {
    fontFamily: '"Onest", sans-serif',
    fontSize: "2.25rem", // 36px
    lineHeight: "2.75rem", // 44px
    letterSpacing: "-0.02em",
    fontWeight: 600,
  },
  displaySmallBold: {
    fontFamily: '"Onest", sans-serif',
    fontSize: "1.875rem", // 30px
    lineHeight: "2.375rem", // 38px
    fontWeight: 700,
  },
  textMediumRegular: {
    fontFamily: '"Onest", sans-serif',
    fontSize: "1rem", // 16px
    lineHeight: "1.5rem", // 24px
    fontWeight: 400,
  },
  textExtraSmallBold: {
    fontFamily: '"Onest", sans-serif',
    fontSize: "0.75rem", // 12px
    lineHeight: "1.125rem", // 18px
    fontWeight: 700,
  },
  textSmallRegular: {
    fontFamily: '"Onest", sans-serif',
    fontSize: "0.875rem", // 14px
    lineHeight: "1.25rem", // 20px
    fontWeight: 400,
  },
}));
const DEFAULT_SETTINGS = {
  // Matches the format used elsewhere in the app.
  lastPull: "10:30 AM EST, Monday, Jan. 07, 2026",
};
const DEFAULT_USER = {
  currentFiscalQuarter: "2026-Q1",
};
const DEFAULT_DATA = {
  "What's Going Well": [
    {
      id: "w1",
      description:
        "EMEA + Mid-Market + Cloud Compress win rate at 43% significantly higher than avg. Build more pipeline here.",
    },
    {
      id: "w2",
      description:
        "Midpoint of Q1 SG projection for North America exceeds 100% of quota.",
    },
    {
      id: "w3",
      description: "Sharp increase in average deal size to $42K in Q4",
    },
    {
      id: "w4",
      description: "North America contributed 45% of Q4 Closed Won",
    },
  ],
  "Needs Attention": [
    {
      id: "n1",
      description:
        "Q1 QTD Demo to Proposal conversion rate (by ACV) really low at 25%",
    },
    {
      id: "n2",
      description:
        "Best case Q1 SG projection for company is less than 80% of quota",
    },
    {
      id: "n3",
      description: "Q2 pipeline for North America is low compared to historical pacing",
    },
    {
      id: "n4",
      description: "Q1 coverage ratio for Latin America very low at 1.0x",
    },
  ],
};
// Format date to "7 Jan 2026" format (expects "..., weekday, Mon. 07, 2026").
function formatLastPullDate(dateString) {
  if (!dateString) return "";
  try {
    const parts = dateString.split(",");
    if (parts.length >= 3) {
      const monthDayPart = parts[parts.length - 2].trim(); // "Jan. 07"
      const yearPart = parts[parts.length - 1].trim(); // "2026"
      const monthDayMatch = monthDayPart.match(/(\w+)\.?\s+0?(\d+)/);
      if (monthDayMatch) {
        const month = monthDayMatch[1];
        const day = parseInt(monthDayMatch[2], 10);
        return `${day} ${month} ${yearPart}`;
      }
    }
    return dateString;
  } catch (_e) {
    return dateString;
  }
}
function renderDataTable(items, headerColor) {
  if (!items || items.length === 0) {
    return (
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #D3D3D3",
          backgroundColor: "#FFFFFF",
          borderRadius: 0,
        }}
        cellPadding="0"
        cellSpacing="0"
      >
        <tbody>
          <tr>
            <td
              colSpan={2}
              style={{
                color: rccColors.neutral[500],
                textAlign: "center",
                padding: "20px",
                fontStyle: "italic",
                fontFamily: '"Onest", sans-serif',
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
              }}
            >
              No insights available
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        border: "1px solid #D3D3D3",
        backgroundColor: "#FFFFFF",
        borderRadius: 0,
      }}
      cellPadding="0"
      cellSpacing="0"
    >
      <thead>
        <tr style={{ backgroundColor: headerColor }}>
          <th
            style={{
              color: "#FFFFFF",
              padding: "8px",
              border: "none",
              textAlign: "center",
              borderRight: "1px solid rgba(255, 255, 255, 0.35)",
              fontFamily: '"Onest", sans-serif',
              fontSize: "0.75rem",
              lineHeight: "1.125rem",
              fontWeight: 700,
            }}
          >
            #
          </th>
          <th
            style={{
              color: "#FFFFFF",
              padding: "8px",
              border: "none",
              textAlign: "center",
              fontFamily: '"Onest", sans-serif',
              fontSize: "0.75rem",
              lineHeight: "1.125rem",
              fontWeight: 700,
            }}
          >
            Description
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => {
          const bgColor = index % 2 === 0 ? "#FFFFFF" : "#F5F5F5";
          const isLastRow = index === items.length - 1;
          return (
            <tr key={item?.id || index} style={{ backgroundColor: bgColor }}>
              <td
                style={{
                  padding: "8px",
                  borderBottom: isLastRow ? "none" : "1px solid #D3D3D3",
                  color: rccColors.neutral[900],
                  textAlign: "center",
                  borderRight: "1px solid #D3D3D3",
                  fontFamily: '"Onest", sans-serif',
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  fontWeight: 400,
                }}
              >
                {index + 1}
              </td>
              <td
                style={{
                  padding: "8px",
                  borderBottom: isLastRow ? "none" : "1px solid #D3D3D3",
                  color: rccColors.neutral[900],
                  fontFamily: '"Onest", sans-serif',
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  fontWeight: 400,
                }}
              >
                {item?.description || ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
export default function PerformanceAlertsMock({
  data: dataOverride,
  settings: settingsOverride,
  user: userOverride,
  loadingDelayMs = 400,
  fail = false,
}) {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const settings = useMemo(
    () => settingsOverride || DEFAULT_SETTINGS,
    [settingsOverride]
  );
  const user = useMemo(() => userOverride || DEFAULT_USER, [userOverride]);
  const fiscalQuarter = user?.currentFiscalQuarter
    ? `${user.currentFiscalQuarter} QTD`
    : "Current Fiscal Quarter";
  useEffect(() => {
    ensureOnestFontLoaded();
    setLoading(true);
    setError(null);
    const t = window.setTimeout(() => {
      if (fail) {
        setError("Failed to load performance insights (mock).");
        setLoading(false);
        return;
      }
      setData(dataOverride || DEFAULT_DATA);
      setLoading(false);
    }, Math.max(0, Number(loadingDelayMs) || 0));
    return () => window.clearTimeout(t);
  }, [dataOverride, loadingDelayMs, fail]);
  if (loading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.loadingContainer}>
          <Skeleton
            variant="text"
            width="40%"
            height={48}
            style={{ margin: "0 auto 16px" }}
          />
          <Skeleton
            variant="text"
            width="60%"
            height={24}
            style={{ margin: "0 auto 32px" }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={300}
            style={{ borderRadius: "8px", marginBottom: "24px" }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={300}
            style={{ borderRadius: "8px" }}
          />
        </Box>
      </Box>
    );
  }
  if (error) {
    return (
      <Box className={classes.container}>
        <Box className={classes.headerSection}>
          <Typography className={`${classes.displaySmallBold} ${classes.title}`}>
            Performance Alerts
          </Typography>
        </Box>
        <Typography className={classes.errorState}>{error}</Typography>
      </Box>
    );
  }
  const needsAttention = data?.["Needs Attention"] || [];
  const whatsGoingWell = data?.["What's Going Well"] || [];
  return (
    <Box className={classes.container}>
      <Box className={classes.headerSection}>
        <Typography
          className={`${classes.displayMediumSemibold} ${classes.title}`}
        >
          Performance Alerts
        </Typography>
        <Typography className={`${classes.textMediumRegular} ${classes.subtitle}`}>
          Proactive notifications highlighting areas that are performing outside
          expected benchmarks, so teams can act early.
        </Typography>
      </Box>
      <Paper className={classes.contentCard} elevation={0}>
        <table
          id="performance-alerts-parent-table"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  padding: "0 0 16px 0",
                  fontFamily: '"Onest", sans-serif',
                  fontSize: "1.125rem",
                  lineHeight: "1.75rem",
                  fontWeight: 600,
                  color: rccColors.blue[800],
                }}
              >
                {fiscalQuarter}
              </td>
            </tr>
            <tr>
              <td
                style={{
                  padding: "0 0 24px 0",
                  fontFamily: '"Onest", sans-serif',
                  fontSize: "1.5rem",
                  lineHeight: "2rem",
                  fontWeight: 600,
                  color: rccColors.neutral[900],
                }}
              >
                Performance Alerts as of {formatLastPullDate(settings?.lastPull)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "0 0 24px 0" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          padding: "0 0 8px 0",
                          fontFamily: '"Onest", sans-serif',
                          fontSize: "1.125rem",
                          lineHeight: "1.75rem",
                          fontWeight: 500,
                          color: rccColors.neutral[900],
                        }}
                      >
                        What's Going Well
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: 0 }}>
                        {renderDataTable(whatsGoingWell, rccColors.green[700])}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td style={{ padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          padding: "0 0 8px 0",
                          fontFamily: '"Onest", sans-serif',
                          fontSize: "1.125rem",
                          lineHeight: "1.75rem",
                          fontWeight: 500,
                          color: rccColors.neutral[900],
                        }}
                      >
                        Needs Attention
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: 0 }}>
                        {renderDataTable(needsAttention, rccColors.orange[700])}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </Paper>
    </Box>
  );
}
